import React, { useEffect, useState, useCallback } from "react";
import { Clock3, Play, RotateCcw, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Zap } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_COLORS = {
  RUNNING: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  SUCCESS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  PARTIAL_SUCCESS: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  FAILED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function StatusBadge({ status }) {
  const s = String(status || "").toUpperCase();
  const cls = STATUS_COLORS[s] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <span className={`inline-flex items-center rounded-full border font-black uppercase tracking-wider text-[10px] px-2 py-0.5 ${cls}`}
      data-testid={`sched-status-${s.toLowerCase() || "none"}`}>{s || "—"}</span>
  );
}

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }
function fmtDur(ms) { if (!ms && ms !== 0) return "—"; return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)}s`; }

function Card({ label, value, icon: Icon, tone = "neutral", testid }) {
  const tones = { neutral: "text-white", emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300", sky: "text-sky-300", indigo: "text-indigo-300" };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformScheduler() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/scheduler");
      setSummary(r.data.summary);
      setHistory(r.data.recent_history || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runOne = async (jobName) => {
    setRunningId(jobName);
    try {
      await api.post(`/platform/scheduler/run/${jobName}`, {});
      toast.success(`Job ${jobName} selesai.`);
      load();
    } catch (e) { notifyError(e, "Job gagal"); }
    finally { setRunningId(null); }
  };

  const runAll = async () => {
    setRunningAll(true);
    try {
      const r = await api.post("/platform/scheduler/run", {});
      toast.success(`${r.data.success}/${r.data.total_jobs} sukses · ${r.data.failed} gagal · ${r.data.partial_success} partial.`);
      load();
    } catch (e) { notifyError(e, "Run All gagal"); }
    finally { setRunningAll(false); }
  };

  const retry = async (id) => {
    try {
      await api.post(`/platform/scheduler/retry/${id}`, {});
      toast.success("Retry selesai.");
      load();
    } catch (e) { notifyError(e); }
  };

  return (
    <PlatformLayout title="Scheduler">
      <div data-testid="platform-scheduler-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <Card label="Last Run" value={fmt(summary?.last_run)} icon={Clock3} tone="sky" testid="sched-summary-last-run" />
          <Card label="Jobs Today" value={summary?.today_runs ?? 0} icon={Zap} tone="indigo" testid="sched-summary-today" />
          <Card label="Running" value={summary?.running ?? 0} icon={Play} tone="sky" testid="sched-summary-running" />
          <Card label="Success" value={summary?.success ?? 0} icon={CheckCircle2} tone="emerald" testid="sched-summary-success" />
          <Card label="Failed" value={summary?.failed ?? 0} icon={XCircle} tone="rose" testid="sched-summary-failed" />
          <Card label="Avg Time" value={fmtDur(summary?.avg_duration_ms)} icon={Clock3} tone="amber" testid="sched-summary-avg" />
        </div>

        {/* Actions */}
        <div className="mb-5 flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={runAll} disabled={runningAll} className="text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/40" data-testid="sched-run-all">
            <Play size={14} className={`mr-1 ${runningAll ? "animate-pulse" : ""}`} />
            {runningAll ? "Menjalankan semua..." : "Run All Daily Jobs"}
          </Button>
          <Button variant="ghost" onClick={load} disabled={loading} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="sched-reload">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
        </div>

        {/* Jobs Table */}
        <div className="rounded-2xl border border-white/10 overflow-hidden mb-5" data-testid="sched-jobs-table">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
            <div className="col-span-3">Job</div>
            <div className="col-span-2">Schedule</div>
            <div className="col-span-2">Last Run</div>
            <div className="col-span-1">Duration</div>
            <div className="col-span-1">Processed</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {(summary?.jobs || []).map((j) => (
            <div key={j.job_name} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center" data-testid={`sched-job-row-${j.job_name}`}>
              <div className="col-span-3 min-w-0">
                <div className="text-sm font-bold text-white truncate">{j.job_name.replaceAll("_", " ")}</div>
                <div className="text-[10px] text-white/40 truncate">{j.description}</div>
              </div>
              <div className="col-span-2 text-[10px] font-mono text-white/80">{j.schedule}</div>
              <div className="col-span-2 text-[10px] text-white/60">{fmt(j.last_finished_at)}</div>
              <div className="col-span-1 text-[10px] font-mono text-white/80">{fmtDur(j.last_duration_ms)}</div>
              <div className="col-span-1 text-xs font-black text-white">{j.last_processed ?? "—"}</div>
              <div className="col-span-1"><StatusBadge status={j.last_status} /></div>
              <div className="col-span-2 flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => runOne(j.job_name)} disabled={runningId === j.job_name}
                  className="text-emerald-300 hover:bg-emerald-500/20 h-7 text-[10px]" data-testid={`sched-run-${j.job_name}`}>
                  <Play size={11} className="mr-1" /> Run
                </Button>
                {j.last_id && (
                  <Button size="sm" variant="ghost" onClick={() => retry(j.last_id)}
                    className="text-sky-300 hover:bg-sky-500/20 h-7 text-[10px]" data-testid={`sched-retry-${j.job_name}`}>
                    <RotateCcw size={11} className="mr-1" /> Retry
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Recent History */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="sched-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-amber-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Recent Job History</div>
            <div className="ml-auto text-[10px] text-white/50">{history.length} entries</div>
          </div>
          {history.length === 0 ? (
            <div className="text-white/40 text-xs italic py-4 text-center" data-testid="sched-history-empty">Belum ada history. Jalankan job pertama Anda.</div>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-2 rounded-lg border border-white/5 bg-black/30 p-2.5" data-testid={`sched-history-${h.id}`}>
                  <StatusBadge status={h.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{h.job_name.replaceAll("_", " ")}</div>
                    <div className="text-[10px] text-white/40 truncate">
                      {fmt(h.started_at)} · {fmtDur(h.duration_ms)} · processed {h.processed ?? 0}
                      {h.failed ? ` · ${h.failed} failed` : ""}
                      {h.error ? ` · ${h.error}` : ""}
                    </div>
                  </div>
                  <button onClick={() => retry(h.id)} className="w-6 h-6 rounded-lg grid place-items-center text-sky-300 hover:bg-sky-500/20" title="Retry" data-testid={`sched-history-retry-${h.id}`}>
                    <RotateCcw size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PlatformLayout>
  );
}
